import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { style } from '../../components/pdf/fontstyles';
import { Bold } from 'lucide-react';

const styles = StyleSheet.create({
  page: {
    padding: 40,
    marginLeft: 20,
    fontSize: 10,
    lineHeight: 1.4,
    fontFamily: 'Helvetica',
    ...style.marathi,
  },
  header: {
    marginBottom: '15pt',
    textAlign: 'center',
    borderBottom: '1pt solid #000',
    paddingBottom: '10pt'
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
    marginTop: 8,
    marginBottom: 10,
    textTransform: 'uppercase'
  },
  subtitle: {
    fontSize: 10,
    fontWeight: 'bold',
    marginBottom: 2
  },
  reference: {
    fontSize: 10,
    textAlign: 'left'
  },
  personalInfoContainer: {
    flexDirection: 'row',
    marginBottom: '15pt',
    justifyContent: 'space-between'
  },
  personalInfoColumn: {
    width: '48%'
  },
  infoRow: {
    flexDirection: 'row',
    marginBottom: '8pt',
    alignItems: 'center'
  },
  infoLabel: {
    width: '100pt',
    fontWeight: 'bold'
  },
  infoValue: {
    flex: 1,
    marginLeft: '5',
    paddingBottom: '3pt'
  },
  // Table styles
  table: {
    width: '100%',
    border: '1pt solid #000',
    marginTop: '15pt',
    marginBottom: '20pt'
  },
  tableHeader: {
    backgroundColor: '#f0f0f0',
    flexDirection: 'row',
    borderBottom: '1pt solid #000'
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '1pt solid #000',
    minHeight: '30pt'
  },
  tableCol: {
    padding: '6pt 4pt',
    borderRight: '1pt solid #000',
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  tableHeaderText: {
    fontWeight: 'bold',
    fontSize: 9
  },
  subHeader: {
    backgroundColor: '#f8f8f8',
    flexDirection: 'row',
    borderBottom: '1pt solid #000'
  },
  subHeaderCol: {
    padding: '3pt',
    borderRight: '1pt solid #000',
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: 8
  },
  // Column widths for landscape
  srNoCol: {
    width: '5%'
  },
  institutionCol: {
    width: '20%'
  },
  designationCol: {
    width: '12%'
  },
  dateCol: {
    width: '10%'
  },
  periodCol: {
    width: '18%'
  },
  dutyPeriodCol: {
    width: '18%'
  },
  remarkCol: {
    width: '7%'
  },
  subCol: {
    width: '33.33%',
    fontSize: 9
  },
  pageNumber: {
    position: 'absolute',
    fontSize: 10,
    bottom: '20pt',
    right: '30pt'
  }
});

const ServiceHistory = ({ result }) => {
  const toMarathiDigits = str => str.replace(/\d/g, d => '०१२३४५६७८९'[d]);
  
  const serviceHistoryData = [
    {
      institution: result.institutionName,
      designation: result.designation,
      startDate: result.dateOfAppointment,
      endDate: result.dateOfRetirement,
      totalYears: result.calculation[0].grossService.years,
      totalMonths: result.calculation[0].grossService.months,
      totalDays: result.calculation[0].grossService.days,
      dutyYears: result.calculation[0].pensionableService.years,
      dutyMonths: result.calculation[0].pensionableService.months,
      dutyDays: result.calculation[0].pensionableService.days,
    },
    {
      institution: 'Total',
      designation: '',
      startDate: '',
      endDate: '',
      totalYears: result.calculation[0].grossService.years,
      totalMonths: result.calculation[0].grossService.months,
      totalDays: result.calculation[0].grossService.days,
      dutyYears: result.calculation[0].pensionableService.years,
      dutyMonths: result.calculation[0].pensionableService.months,
      dutyDays: result.calculation[0].pensionableService.days,
    },
  ];

  return (
    <View size="A4" orientation='landscape' style={styles.page}>
      {/* Header Section */}
      <View style={styles.header}>
        <Text style={styles.subtitle}>Pension Scheme for Teaching and Non-Teaching Staff</Text>
        <Text style={styles.subtitle}>Non-Government Aided College and Non-Agricultural University</Text>
        <Text style={[styles.title]}>
          Service History
        </Text>
        <Text style={[styles.subtitle]}>
          Statement showing the service history of Shri/Smt. {result.employeeName}, Designation: {result.designation}
        </Text>
      </View>

      {/* Personal Information Section - Now in two columns */}
      <View style={styles.personalInfoContainer}>
        <View style={styles.personalInfoColumn}>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Name of College:</Text>
            <Text style={styles.infoValue}>{result.institutionName}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Date of Birth:</Text>
            <Text style={styles.infoValue}>{result.dateOfBirth}</Text>
          </View>
        </View>
        <View style={styles.personalInfoColumn}>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Date of Appointment:</Text>
            <Text style={styles.infoValue}>{result.dateOfAppointment}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Date of Retirement:</Text>
            <Text style={styles.infoValue}>{result.dateOfRetirement}</Text>
          </View>
        </View>
      </View>

      {/* Service History Table */}
      <View style={styles.table}>
        {/* Main Header Row */}
        <View style={styles.tableHeader}>
          <View style={[styles.tableCol, styles.srNoCol]}>
            <Text style={styles.tableHeaderText}>Sr. No.</Text>
          </View>
          <View style={[styles.tableCol, styles.institutionCol]}>
            <Text style={styles.tableHeaderText}>Name of Institution</Text>
          </View>
          <View style={[styles.tableCol, styles.designationCol]}>
            <Text style={styles.tableHeaderText}>Designation</Text>
          </View>
          <View style={[styles.tableCol, styles.dateCol]}>
            <Text style={styles.tableHeaderText}>Date of Joining</Text>
          </View>
          <View style={[styles.tableCol, styles.dateCol]}>
            <Text style={styles.tableHeaderText}>Date of Leaving</Text>
          </View>
          <View style={[styles.tableCol, styles.periodCol]}>
            <Text style={styles.tableHeaderText}>Total Service Period</Text>
          </View>
          <View style={[styles.tableCol, styles.dutyPeriodCol]}>
            <Text style={styles.tableHeaderText}>Duty Period Counting</Text>
          </View>
          <View style={[styles.tableCol, styles.remarkCol]}>
            <Text style={styles.tableHeaderText}>Remarks</Text>
          </View>
        </View>

        {/* Sub Header Row */}
        <View style={styles.subHeader}>
          <View style={[styles.tableCol, styles.srNoCol, {backgroundColor: 'transparent'}]}></View>
          <View style={[styles.tableCol, styles.institutionCol, {backgroundColor: 'transparent'}]}></View>
          <View style={[styles.tableCol, styles.designationCol, {backgroundColor: 'transparent'}]}></View>
          <View style={[styles.tableCol, styles.dateCol, {backgroundColor: 'transparent'}]}></View>
          <View style={[styles.tableCol, styles.dateCol, {backgroundColor: 'transparent'}]}></View>
          {/* Sub-columns for Total Period */}
          <View style={{width: '18%', flexDirection: 'row'}}>
            <View style={[styles.subHeaderCol, styles.subCol]}><Text>Years</Text></View>
            <View style={[styles.subHeaderCol, styles.subCol]}><Text>Months</Text></View>
            <View style={[styles.subHeaderCol, styles.subCol]}><Text>Days</Text></View>
          </View>
          {/* Sub-columns for Duty Period */}
          <View style={{width: '18%', flexDirection: 'row'}}>
            <View style={[styles.subHeaderCol, styles.subCol]}><Text>Years</Text></View>
            <View style={[styles.subHeaderCol, styles.subCol]}><Text>Months</Text></View>
            <View style={[styles.subHeaderCol, styles.subCol]}><Text>Days</Text></View>
          </View>
          <View style={[styles.tableCol, styles.remarkCol, {backgroundColor: 'transparent'}]}></View>
        </View>

        {/* Table Data Rows */}
        {serviceHistoryData.map((row, index) => (
          <View style={styles.tableRow} key={index}>
            <View style={[styles.tableCol, styles.srNoCol]}>
              <Text>{index + 1}</Text>
            </View>
            <View style={[styles.tableCol, styles.institutionCol]}>
              <Text>{row.institution}</Text>
            </View>
            <View style={[styles.tableCol, styles.designationCol]}>
              <Text>{row.designation}</Text>
            </View>
            <View style={[styles.tableCol, styles.dateCol]}>
              <Text>{row.startDate}</Text>
            </View>
            <View style={[styles.tableCol, styles.dateCol]}>
              <Text>{row.endDate}</Text>
            </View>
            {/* Total Period Sub-columns */}
            <View style={{width: '18%', flexDirection: 'row'}}>
              <View style={[styles.tableCol, styles.subCol]}><Text>{row.totalYears}</Text></View>
              <View style={[styles.tableCol, styles.subCol]}><Text>{row.totalMonths}</Text></View>
              <View style={[styles.tableCol, styles.subCol]}><Text>{row.totalDays}</Text></View>
            </View>
            {/* Duty Period Sub-columns */}
            <View style={{width: '18%', flexDirection: 'row'}}>
              <View style={[styles.tableCol, styles.subCol]}><Text>{row.dutyYears}</Text></View>
              <View style={[styles.tableCol, styles.subCol]}><Text>{row.dutyMonths}</Text></View>
              <View style={[styles.tableCol, styles.subCol]}><Text>{row.dutyDays}</Text></View>
            </View>
            <View style={[styles.tableCol, styles.remarkCol]}>
              <Text></Text>
            </View>
          </View>
        ))}
      </View>

      {/* Page Number */}
      <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => (
        `Page ${pageNumber} of ${totalPages}`
      )} fixed />
    </View>
  );
};

export default ServiceHistory;